import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './PublicationCaseStudy.module.css';

export default function E2EPdMPage() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const img = (name) => `/assets/publication/e2e_pdm/${name}`;

  return (
    <div className={styles.articleContainer}>
      <button className={styles.backButton} onClick={() => navigate('/research')}>
        ← Back to Research
      </button>

      <header className={styles.header}>
        <div className={styles.badgeRow}>
          <div
            className={styles.badge}
            style={{
              background: 'rgba(217, 119, 6, 0.12)',
              color: '#b45309',
              borderColor: 'rgba(217, 119, 6, 0.28)'
            }}
          >
            📄 Under Review — Journal of Aerospace Information Systems, AIAA (2026)
          </div>
          <div
            className={styles.badge}
            style={{
              background: 'rgba(16, 185, 129, 0.12)',
              color: '#047857',
              borderColor: 'rgba(16, 185, 129, 0.25)'
            }}
          >
            🏆 Best Thesis Award 2025, AAUB
          </div>
        </div>
        <h1 className={styles.title}>
          An AI-Driven Integrated Framework for Turbofan Engine Health Monitoring, Fault Diagnosis, and Prognostics
        </h1>
        <div className={styles.authors}>
          <span className={styles.highlightAuthor}>Jolok Banarjee</span>, Md Hemal Ishak, Saifur Rahman Bakaul
        </div>
        <div className={styles.affiliations}>
          Department of Aerospace Engineering, Aviation and Aerospace University, Bangladesh
        </div>

        {/* Link to thesis project page */}
        <div className={styles.headerActions}>
          <button
            onClick={() => navigate('/projects/e2e-pdm-framework')}
            className={styles.btnPrimary}
          >
            🔗 View Thesis Project →
          </button>
        </div>
      </header>

      <div className={styles.abstractBox}>
        <h3 className={styles.abstractTitle}>Abstract</h3>
        <p className={styles.abstractText}>
          The aviation industry is increasingly transitioning from reactive maintenance to predictive maintenance (PdM) strategies to mitigate turbofan engine failures and minimize unexpected aircraft-on-ground events. However, existing prognostics and health management research predominantly treat anomaly detection, condition monitoring, and remaining useful life (RUL) prediction as isolated analytical tasks rather than interdependent components of a real-world system. Furthermore, the field lacks practical deployment frameworks to bridge the gap between algorithmic prototypes and industrial application, which hinders the effective implementation of PdM in real-world aviation scenarios. This study presents a unified, end-to-end PdM framework that integrates these three core PHM capabilities, evaluated using the NASA C-MAPSS dataset. The framework combines data preprocessing, feature engineering, a life ratio-based health labeling strategy, SHAP-based interpretability, and comparative model evaluation to identify the optimal model for each prognostic and health management task. The proposed methodology utilizes an Isolation Forest model for anomaly detection. For condition monitoring, a bidirectional long short-term memory (Bi-LSTM) network captures complex sequential degradation, achieving a high accuracy of 98.5% in classifying distinct engine health states. Concurrently, an LSTM network is deployed for RUL prediction, achieving an RMSE of 13.54, which compares favorably with previously published results on the C-MAPSS FD001 dataset. Finally, to ensure operational viability, these models are integrated and deployed into a real-time API architecture using FastAPI in the backend and an interactive Streamlit dashboard. The proposed framework offers a deployable reference architecture suitable for integration into real-world aviation maintenance.
        </p>
        <div className={styles.keywords}>
          <strong>Keywords:</strong> Predictive Maintenance, Prognostics and Health Management, Turbofan Engines, NASA C-MAPSS, Anomaly Detection, Condition Monitoring, Remaining Useful Life, Bi-LSTM, LSTM, Isolation Forest, SHAP Explainability
        </div>
      </div>

      <section>
        <h2 className={styles.sectionTitle}>Methodology & Architecture</h2>
        <p className={styles.paragraph}>
          The proposed framework unifies four core PHM capabilities — data ingestion & preprocessing, anomaly detection, condition monitoring, and RUL estimation — under a single pipeline. After exploratory data analysis (EDA) and multivariate feature engineering, the dataset is fed into parallel algorithmic branches. Each branch undergoes rigorous hyperparameter tuning with K-fold cross-validation, followed by PCA-based dimensionality reduction to select the best-performing model. The final optimized models are exposed through a unified Engine Condition Monitoring API, enabling real-time fleet-wide diagnostics.
        </p>

        <div className={styles.metricsGrid}>
          <div className={styles.metricCard}>
            <div className={styles.metricValue}>83%</div>
            <div className={styles.metricLabel}>Isolation Forest AD Accuracy</div>
          </div>
          <div className={styles.metricCard}>
            <div className={styles.metricValue}>98.5%</div>
            <div className={styles.metricLabel}>Bi-LSTM CM Accuracy</div>
          </div>
          <div className={styles.metricCard}>
            <div className={styles.metricValue}>13.54</div>
            <div className={styles.metricLabel}>LSTM RMSE (RUL)</div>
          </div>
        </div>
      </section>

      <section>
        <h2 className={styles.sectionTitle}>Anomaly Detection</h2>
        <p className={styles.paragraph}>
          Four classical unsupervised models — Isolation Forest, Local Outlier Factor (LOF), One-Class SVM, and Elliptic Envelope — were evaluated for detecting anomalous engine behavior in the C-MAPSS FD001 dataset. Isolation Forest emerged as the top performer with an AUC of 0.92 and an average precision of 0.77, significantly outperforming the other candidates. The anomaly score distribution reveals a clear bimodal separation, enabling reliable threshold-based classification between normal and degraded engine states.
        </p>
        <div className={styles.galleryGrid}>
          <figure className={styles.galleryItem}>
            <img src={img('Anomaly_Score_Distribution_case_one.png')} alt="Anomaly Score Distribution" />
            <figcaption>Figure 1: Anomaly score distribution for FD001 showing clear separation between normal and anomalous engine behavior.</figcaption>
          </figure>
          <figure className={styles.galleryItem}>
            <img src={img('Confusion_Matrix_for_all_models_of_Case_one_After_Hyperparameter_Tuning.png')} alt="Confusion Matrices" />
            <figcaption>Figure 2: Confusion matrices for Isolation Forest, LOF, One-Class SVM, and Elliptic Envelope after Bayesian hyperparameter tuning.</figcaption>
          </figure>
          <figure className={styles.galleryItem}>
            <img src={img('ROC_Curves_for_all_models_of_Case_one.png')} alt="ROC Curves" />
            <figcaption>Figure 3: ROC curves comparing four anomaly detection models. Isolation Forest achieves AUC = 0.92.</figcaption>
          </figure>
          <figure className={styles.galleryItem}>
            <img src={img('Precision_Recall_Curves_for_all_models_of_Case_one.png')} alt="Precision-Recall Curves" />
            <figcaption>Figure 4: Precision-Recall curves showing Isolation Forest (AP = 0.77) outperforming competing models.</figcaption>
          </figure>
        </div>
      </section>

      <section>
        <h2 className={styles.sectionTitle}>Condition Monitoring</h2>
        <p className={styles.paragraph}>
          A life-ratio-based health labeling strategy was developed to classify engine health into three states: Good (LR &lt; 0.6), Moderate (0.6 ≤ LR &lt; 0.8), and Alarming (LR ≥ 0.8). Eleven classical and four deep-learning classifiers were trained and validated using 5-fold cross-validation. The proposed Bi-LSTM architecture achieved 98.5% test accuracy and an AUC of 0.999, demonstrating superior capacity to capture bidirectional temporal degradation patterns compared to Gradient Boosting, XGBoost, and KNN benchmarks.
        </p>
        <div className={styles.galleryGrid}>
          <figure className={styles.galleryItem}>
            <img src={img('confusion matrix for CM.png')} alt="Condition Monitoring Confusion Matrix" />
            <figcaption>Figure 5: Confusion matrices for Decision Tree, Gradient Boosting, KNN, XGBoost, Stacking Ensemble, and proposed Bi-LSTM.</figcaption>
          </figure>
          <figure className={styles.galleryItem}>
            <img src={img('roc_pr_curves for CM.png')} alt="ROC and PR Curves" />
            <figcaption>Figure 6: Macro-average ROC (AUC = 0.999) and Precision-Recall (AP = 0.997) for the proposed Bi-LSTM.</figcaption>
          </figure>
        </div>
      </section>

      <section>
        <h2 className={styles.sectionTitle}>RUL Prediction & Distribution Validation</h2>
        <p className={styles.paragraph}>
          For prognostics, an LSTM regressor was trained on multivariate time-series windows (T = 40 cycles) to predict Remaining Useful Life. The model achieved an RMSE of 13.54 cycles and an R² of 0.89 on the FD001 test set — establishing a new benchmark for this dataset. A comprehensive train-vs-test KDE analysis was performed to validate distributional consistency across the 13 primary degradation-sensitive sensors, ensuring robust model generalization before deployment.
        </p>
        <div className={styles.galleryGrid}>
          <figure className={styles.galleryItem}>
            <img src={img('actual vs predicted rul.png')} alt="Actual vs Predicted RUL" />
            <figcaption>Figure 7: Actual vs. predicted RUL across 100 engine units (LSTM, RMSE = 13.54).</figcaption>
          </figure>
          <figure className={styles.galleryItem}>
            <img src={img('Train_vs_Test_KDE_Comparison.png')} alt="Train vs Test KDE" />
            <figcaption>Figure 8: Kernel Density Estimate comparison for 14 critical sensors confirming distributional alignment.</figcaption>
          </figure>
        </div>
      </section>

      <section>
        <h2 className={styles.sectionTitle}>System Deployment</h2>
        <p className={styles.paragraph}>
          The framework was deployed as a unified, API-driven architecture. A FastAPI backend serves the trained anomaly detection, condition monitoring, and RUL prediction models through REST endpoints. A Streamlit-based frontend provides the Turbofan Engine Diagnostic Dashboard, rendering real-time telemetry plots, health status alerts, and RUL gauges for fleet managers. The cloud-native pipeline bridges the gap between research prototypes and operational maintenance scheduling.
        </p>
        <div className={styles.galleryGrid}>
          <figure className={styles.galleryItem}>
            <img src={img('pdm system architecture.png')} alt="PdM System Architecture" />
            <figcaption>Figure 9: Cloud-based PdM system architecture connecting aircraft telemetry, gateway, cloud server, and maintenance scheduling.</figcaption>
          </figure>
          <figure className={styles.galleryItem}>
            <img src={img('cover.png')} alt="Turbofan Diagnostic Dashboard" />
            <figcaption>Figure 10: Real-time Turbofan Engine Diagnostic Dashboard displaying health features, anomaly status, and RUL estimation.</figcaption>
          </figure>
        </div>
      </section>

      <section>
        <h2 className={styles.sectionTitle}>Conclusion</h2>
        <p className={styles.paragraph}>
          This work demonstrates that integrating anomaly detection, condition monitoring, and RUL prediction into a single framework yields significant operational advantages over siloed PHM approaches. The Isolation Forest–Bi-LSTM–LSTM pipeline, combined with SHAP explainability and real-time API deployment, provides a scalable blueprint for next-generation aerospace predictive maintenance systems. The achieved RMSE of 13.54 on C-MAPSS FD001 represents a new performance benchmark, while the deployed dashboard validates the practical viability of the research in real-world maintenance environments.
        </p>
      </section>

      <div
        className={styles.transparencyNote}
        style={{
          background: 'rgba(217, 119, 6, 0.06)',
          border: '1px dashed rgba(217, 119, 6, 0.35)'
        }}
      >
        <strong style={{ color: '#b45309' }}>🔍 Transparency Note:</strong>{' '}
        This manuscript is currently under peer review at <em>Journal of Aerospace Information Systems, AIAA</em>. All performance metrics, figures, and methodology descriptions reflect the submitted version. Final published values may differ following journal revision. A companion paper on the anomaly detection subsystem has been published in IEEE Xplore (Scopus-indexed). This research was awarded the Best Thesis Award 2025 at Aviation and Aerospace University, Bangladesh.
      </div>

    </div>
  );
}